import { GoogleAuthProvider, signInWithPopup, signOut as firebaseSignOut, onAuthStateChanged, getIdToken } from 'firebase/auth';
/**
 * AuthService handles all authentication logic:
 * - Firebase authentication
 * - Token management and refresh
 * - Backend authentication
 * - Auth state monitoring
 */
export class AuthService {
    constructor(auth, logger) {
        this.logger = logger;
        this.listeners = new Set();
        /** Memoized in-flight refresh \u2014 ensures parallel callers share a single
         *  Firebase token-refresh request instead of stampeding it. */
        this.refreshInFlight = null;
        this.auth = auth;
        this.authState = {
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: true
        };
        this.authStateUnsubscribe = this.setupFirebaseAuthListener();
    }
    /**
     * Sign in with Google
     */
    async signInWithGoogle() {
        try {
            const provider = new GoogleAuthProvider();
            // Add additional scopes if needed
            provider.addScope('email');
            provider.addScope('profile');
            // Set custom parameters
            provider.setCustomParameters({
                'prompt': 'select_account'
            });
            const result = await signInWithPopup(this.auth, provider);
            return this.convertFirebaseUser(result.user);
        }
        catch (error) {
            const firebaseError = error;
            // User-initiated cancellations - not errors, just return null
            if (firebaseError.code === 'auth/popup-closed-by-user') {
                this.logger.info('User cancelled sign-in');
                return null;
            }
            // Log actual errors
            this.logger.error('Error signing in with Google:', error);
            // Popup blocked - friendly guidance
            if (firebaseError.code === 'auth/popup-blocked') {
                throw new Error('Please allow pop-ups for this site and try again.');
            }
            // Configuration error - needs admin attention
            if (firebaseError.code === 'auth/configuration-not-found') {
                throw new Error('Google Sign-In is not properly configured for this Firebase project. Please check the Firebase Console.');
            }
            throw error;
        }
    }
    /**
     * Sign out
     */
    async signOut() {
        try {
            await firebaseSignOut(this.auth);
            this.logger.info('User signed out successfully');
        }
        catch (error) {
            this.logger.error('Sign out failed:', error);
            throw error;
        }
    }
    /**
     * Refresh the current user's token. Concurrency-safe: parallel callers
     * share a single in-flight refresh promise so we don't stampede Firebase
     * Auth when multiple API calls hit a 401 simultaneously.
     */
    async refreshToken() {
        if (this.refreshInFlight) {
            return this.refreshInFlight;
        }
        const promise = this.doRefreshToken();
        this.refreshInFlight = promise;
        try {
            return await promise;
        }
        finally {
            // Clear regardless of success/failure so the next attempt can try
            // again (and fail fast on its own merits).
            this.refreshInFlight = null;
        }
    }
    async doRefreshToken() {
        try {
            this.logger.info('Refreshing authentication token');
            if (!this.auth.currentUser) {
                throw new Error('No authenticated user found');
            }
            const newToken = await getIdToken(this.auth.currentUser, true); // Force refresh
            if (!newToken) {
                throw new Error('Failed to refresh token');
            }
            // Update auth state with new token
            this.updateAuthState({ token: newToken });
            this.logger.info('Token refreshed successfully');
            return newToken;
        }
        catch (error) {
            this.logger.error('Token refresh failed:', error);
            // If token refresh fails, sign out the user
            await this.signOut();
            throw error;
        }
    }
    /**
     * Get current auth state
     */
    getAuthState() {
        return { ...this.authState };
    }
    /**
     * Get current token
     */
    getCurrentToken() {
        return this.authState.token;
    }
    /**
     * Get the current user's ID token
     */
    async getCurrentUserToken(forceRefresh = false) {
        try {
            const user = this.auth.currentUser;
            if (!user)
                return null;
            return await user.getIdToken(forceRefresh);
        }
        catch (error) {
            this.logger.error('Error getting user token:', error);
            return null;
        }
    }
    /**
     * Get current user
     */
    getCurrentUser() {
        return this.authState.user;
    }
    /**
     * Expose the underlying Firebase Auth instance for advanced flows
     * (e.g. phone-number linking via `linkWithPhoneNumber` + `RecaptchaVerifier`).
     * Use sparingly — most code should go through this service.
     */
    getFirebaseAuth() {
        return this.auth;
    }
    /**
     * Check if user is authenticated
     */
    isAuthenticated() {
        return this.authState.isAuthenticated;
    }
    /**
     * Subscribe to auth state changes
     */
    onAuthStateChanged(listener) {
        this.listeners.add(listener);
        // Immediately call with current state
        listener(this.getAuthState());
        // Return unsubscribe function
        return () => {
            this.listeners.delete(listener);
        };
    }
    /**
     * Wait for auth to be ready
     */
    async waitForAuth() {
        return new Promise((resolve) => {
            const unsubscribe = this.onAuthStateChanged((state) => {
                if (!state.isLoading) {
                    unsubscribe();
                    resolve(state.user);
                }
            });
        });
    }
    /**
     * Clean up resources
     */
    shutdown() {
        if (this.authStateUnsubscribe) {
            this.authStateUnsubscribe();
        }
        this.listeners.clear();
        this.logger.info('AuthService shut down');
    }
    // ===============================
    // Private Methods
    // ===============================
    /**
     * Set up Firebase auth state listener
     */
    setupFirebaseAuthListener() {
        return onAuthStateChanged(this.auth, async (firebaseUser) => {
            try {
                if (!firebaseUser) {
                    this.logger.info('User signed out');
                    this.handleUserSignOut();
                    return;
                }
                const user = this.convertFirebaseUser(firebaseUser);
                this.logger.info('User signed in:', user.email);
                await this.handleUserSignIn(user);
            }
            catch (error) {
                this.logger.error('Error in Firebase auth state change:', error);
                this.updateAuthState({
                    isLoading: false,
                    isAuthenticated: false
                });
            }
        });
    }
    /**
     * Handle user sign in from Firebase
     */
    async handleUserSignIn(user) {
        try {
            // Get Firebase token
            const token = await getIdToken(this.auth.currentUser, false);
            if (!token) {
                throw new Error('Failed to get auth token');
            }
            // Update auth state
            await this.updateAuthState({
                user,
                token,
                isAuthenticated: true,
                isLoading: false
            });
            this.logger.info('User authentication completed successfully');
        }
        catch (error) {
            this.logger.error('Error handling user sign in:', error);
            this.updateAuthState({
                isLoading: false,
                isAuthenticated: false
            });
            throw error;
        }
    }
    /**
     * Handle user sign out from Firebase
     */
    handleUserSignOut() {
        this.updateAuthState({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false
        });
    }
    /**
     * Update auth state and notify listeners
     */
    async updateAuthState(updates) {
        this.authState = { ...this.authState, ...updates };
        // Notify all listeners
        const currentState = this.getAuthState();
        this.listeners.forEach(async (listener) => {
            try {
                await listener(currentState);
            }
            catch (error) {
                this.logger.error('Error in auth state listener:', error);
            }
        });
    }
    /**
     * Convert Firebase User to our AuthUser interface
     */
    convertFirebaseUser(firebaseUser) {
        return {
            // Firebase's raw uid string is our UserId — branded once at this auth boundary.
            uid: firebaseUser.uid,
            email: firebaseUser.email || 'unknown',
            displayName: firebaseUser.displayName || 'Unknown',
            photoURL: firebaseUser.photoURL || ''
        };
    }
}
